import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';
import { logger } from '../utils/logger';

export interface SLAAlert {
  id: string;
  quote_id?: string;
  carrier_id?: string;
  alert_type: string;
  hours_overdue: number;
  status: string;
  created_at: string;
  resolved_at?: string | null;
  resolved_by?: string | null;
  notes?: string | null;
  updated_at?: string;
  viewed_at?: string | null;
  quotes?: {
    id: string;
    name: string;
    email: string;
    status: string;
  };
  carrier_registrations?: {
    id: string;
    company_name: string;
    email: string;
  };
}

export const useSLAAlerts = (status?: 'active' | 'all', unreadOnly?: boolean) => {
  return useQuery({
    queryKey: ['sla-alerts', status, unreadOnly],
    queryFn: async () => {
      // Construir la consulta base
      let query = supabase
        .from('sla_alerts')
        .select(`
          *,
          quotes(id, name, email, status),
          carrier_registrations(id, company_name, email)
        `);

      // Aplicar filtros en orden específico
      // Primero el filtro de status
      if (status === 'active') {
        query = query.eq('status', 'active');
      }

      // Luego el filtro de viewed_at si es necesario
      // Usar una construcción más explícita para evitar problemas con null
      if (unreadOnly === true) {
        query = query.is('viewed_at', null);
      }

      // Aplicar ordenamiento
      query = query.order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) {
        logger.error('Error fetching SLA alerts:', error);
        throw error;
      }
      
      return data as SLAAlert[];
    },
    refetchInterval: 60000, // Refrescar cada minuto
    staleTime: 30000, // Considerar datos frescos por 30 segundos
  });
};

// Hook para marcar alerta como vista
export const useMarkAlertAsViewed = () => {
  const markAsViewed = async (alertId: string) => {
    const { error } = await supabase.rpc('mark_sla_alert_as_viewed', {
      alert_id: alertId
    });
    if (error) throw error;
  };

  const markMultipleAsViewed = async (alertIds: string[]) => {
    const { error } = await supabase.rpc('mark_sla_alerts_as_viewed', {
      alert_ids: alertIds
    });
    if (error) throw error;
  };

  return { markAsViewed, markMultipleAsViewed };
};

// Hook para resolver/dismiss alertas
export const useResolveSLAAlert = () => {
  const resolveAlert = async (alertId: string, notes?: string) => {
    const { data, error } = await supabase.rpc('resolve_sla_alert', {
      alert_id: alertId,
      alert_notes: notes || null
    });
    if (error) {
      logger.error('RPC resolve_sla_alert error:', error);
      throw new Error(error.message || 'Failed to resolve alert');
    }
    return data;
  };

  const dismissAlert = async (alertId: string, notes?: string) => {
    const { data, error } = await supabase.rpc('dismiss_sla_alert', {
      alert_id: alertId,
      alert_notes: notes || null
    });
    if (error) {
      logger.error('RPC dismiss_sla_alert error:', error);
      throw new Error(error.message || 'Failed to dismiss alert');
    }
    return data;
  };

  return { resolveAlert, dismissAlert };
};

export const getAlertTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    quote_response: 'Quote No Response',
    quote_assignment: 'Quote No Assignment',
    carrier_response: 'Carrier Not Verified',
    pickup_delay: 'Pickup Delay',
    delivery_delay: 'Delivery Delay',
  };
  return labels[type] || type;
};

export const getSeverityColor = (hours: number): string => {
  if (hours > 48) return 'text-red-600 bg-red-50 border-red-200';
  if (hours > 36) return 'text-orange-600 bg-orange-50 border-orange-200';
  return 'text-yellow-600 bg-yellow-50 border-yellow-200';
};

